import { createContext, useContext, useEffect, useReducer, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { TOOLKIT_VERSION, type GameModule, type Toolkit } from './toolkit';

import { termOf } from './helpers';

export { shuffle, pick, splitFace, spokenText, termOf, ORDER_SEPARATOR } from './helpers';
export type { Verdict } from './toolkit';

const KitContext = createContext<Toolkit | null>(null);

export function useKit(): Toolkit {
  const kit = useContext(KitContext);
  if (!kit) throw new Error('A game must run inside a game host');
  return kit;
}

export function defineReactGame(Component: ComponentType): GameModule {
  return {
    toolkit: TOOLKIT_VERSION,
    mount(el, kit) {
      const box = document.createElement('div');
      el.appendChild(box);
      const root = createRoot(box);
      root.render(<KitContext.Provider value={kit}><Component /></KitContext.Provider>);
      return {
        unmount() {
          window.setTimeout(() => { root.unmount(); box.remove(); }, 0);
        },
      };
    },
  };
}

export type GameProps = Record<string, unknown>;

export function useGame(_props?: GameProps) {
  const kit = useKit();
  const config = kit.content as Record<string, unknown>;
  const str = (key: string, fallback: string) => {
    const v = config[key];
    return typeof v === 'string' ? v : fallback;
  };
  const num = (key: string, fallback: number) => {
    const v = config[key];
    return typeof v === 'number' ? v : fallback;
  };
  const bool = (key: string, fallback: boolean) => {
    const v = config[key];
    return typeof v === 'boolean' ? v : fallback;
  };
  return {
    kit,
    config,
    live: kit.live,
    say: kit.speech.say,
    register: kit.trials.register,
    respond: kit.trials.respond,
    elapsedSince: kit.clock.elapsedSince,
    str,
    num,
    bool,
    errorless: str('errorless', 'off') === 'on',
    textPrompt: str('promptMode', 'AUDIO') === 'TEXT',
    transitionMs: kit.prefs.transitionMs,
    onBreak: false,
    settings: { reduceMotion: kit.prefs.reduceMotion },
  };
}

export type Game = ReturnType<typeof useGame>;

export function GameFrame({ empty, emptyText, onResume, breakTrial, children }: {
  game: Game;
  empty: boolean;
  emptyText: string;
  onResume: () => void;
  breakTrial: string | null;
  children: ReactNode;
}) {
  const kit = useKit();
  const resume = useRef(onResume);
  resume.current = onResume;
  useEffect(() => { kit.session.status(empty ? 'empty' : 'playing'); }, [kit, empty]);
  useEffect(() => { kit.session.current(breakTrial); }, [kit, breakTrial]);
  useEffect(() => kit.session.onResume(() => resume.current()), [kit]);
  if (empty) {
    return (
      <div className="card">
        <p>{kit.t('This program has nothing to practise yet.')}</p>
        <p className="field-hint">{emptyText}</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function usePaused(): boolean {
  const kit = useKit();
  const [paused, setPaused] = useState(kit.clock.paused());
  useEffect(() => kit.clock.onPauseChange(setPaused), [kit]);
  return paused;
}

export function PromptLine({ game, text, onRepeat }: { game: Game; text: string; onRepeat?: () => void }) {
  const kit = useKit();
  const paused = usePaused();
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused && !game.textPrompt) (onRepeat ?? (() => game.say(text)))();
    wasPaused.current = paused;
  }, [paused]);
  return (
    <div className="g-prompt" aria-live="polite">
      <span className="g-prompt-text">{text}</span>
      {!game.textPrompt && (
        <button type="button" className="g-say" aria-label={kit.t('Say it again')} onClick={onRepeat ?? (() => game.say(text))}>
          <span aria-hidden="true">🔊</span>
        </button>
      )}
    </div>
  );
}

export function useRoundTimer(done: boolean, transitionMs: number, next: () => void, hold = 700) {
  const nextRef = useRef(next);
  nextRef.current = next;
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (!done) { setFading(false); return; }
    const a = window.setTimeout(() => setFading(true), hold);
    const b = window.setTimeout(() => { setFading(false); nextRef.current(); }, hold + transitionMs);
    return () => { window.clearTimeout(a); window.clearTimeout(b); };
  }, [done, transitionMs, hold]);
  return fading;
}

export function Pic({ text, fallback, className }: { text: string; fallback: ReactNode; className?: string }) {
  const kit = useKit();
  const [, refresh] = useReducer((n: number) => n + 1, 0);
  useEffect(() => kit.media.onChange(refresh), [kit]);
  const image = kit.media.picture(text);
  if (!image) return <>{fallback}</>;
  return <img className={`custom-pic${className ? ` ${className}` : ''}`} src={image} alt="" aria-hidden="true" draggable={false} />;
}

export function FaceText({ text }: { text: string }) {
  const kit = useKit();
  const [, refresh] = useReducer((n: number) => n + 1, 0);
  useEffect(() => kit.media.onChange(refresh), [kit]);
  const image = kit.media.picture(text);
  if (!image) return <>{text}</>;
  return (
    <span className="face-text">
      <img className="custom-pic g-tile-img" src={image} alt="" aria-hidden="true" draggable={false} />
      <span>{termOf(text)}</span>
    </span>
  );
}

export function useRoundStyle() {
  const kit = useKit();
  return {
    style: { '--round-transition-ms': `${kit.prefs.transitionMs}ms` } as React.CSSProperties,
    typeClass: `round-${kit.prefs.transitionType.toLowerCase()}`,
  };
}
